import {
  Ellipsis,
  GenericTable,
  GenericTableVariant,
  Modal,
  NewButton,
  NewButtonVariant,
  TooltipTrigger,
} from "@visiblethread/react-components";
import { getDocumentIcon } from "components/Modal/ChooseFilesModal/utils";
import { modalTypes } from "constants/types";
// Branded status icons (coloured project assets, not in lucide-react).
import { ReactComponent as FailIcon } from "icons/danger.svg";
import { ReactComponent as SuccessIcon } from "icons/green-circle-check.svg";
import { ReactComponent as WaitingIcon } from "icons/grey-circle-dash.svg";
import { ReactComponent as InProgressIcon } from "icons/indexing.svg";
import PropTypes from "prop-types";
import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hideModal } from "slices/app/actions/modals";
import {
  dismiss,
  ReindexFileStatus,
  ReindexTerminalState,
  selectReindexProgress,
} from "slices/visi/reindexProgressSlice";

import styles from "./CollectionReindexProgressModal.module.scss";

const IN_PROGRESS_TITLE = "Preparing Your Collection";
const IN_PROGRESS_DESCRIPTION =
  "We're refreshing files so your chat has the latest content.";

const TITLES = {
  [ReindexTerminalState.SUCCESS]: IN_PROGRESS_TITLE,
  [ReindexTerminalState.PARTIAL]: "Some Files Couldn't Be Processed",
  [ReindexTerminalState.FAILED]: "No Files Could Be Processed",
};

const DESCRIPTIONS = {
  [ReindexTerminalState.SUCCESS]: IN_PROGRESS_DESCRIPTION,
  [ReindexTerminalState.PARTIAL]:
    "Your chat will run with the files that are ready. The skipped files won't be included this time.",
  [ReindexTerminalState.FAILED]:
    "None of the files could be processed. Please try again in a moment.",
};

const FOOTER_HINTS = {
  IN_PROGRESS: "Processing",
  [ReindexTerminalState.SUCCESS]: "Sync complete",
  [ReindexTerminalState.PARTIAL]: "Some files were skipped",
  [ReindexTerminalState.FAILED]: "Sync failed",
};

const SOURCE_LABELS = {
  LOCAL: "Local",
  SHAREPOINT: "SharePoint",
};

const AUTO_CLOSE_DELAY_MS = 500;

const formatSource = (source) => {
  if (!source) {
    return "";
  }
  return SOURCE_LABELS[source] ?? source;
};

const getStatusIcon = (status, reason) => {
  switch (status) {
    case ReindexFileStatus.READY:
      return <SuccessIcon />;
    case ReindexFileStatus.PROCESSING:
      return <InProgressIcon className={styles.inProgress} />;
    case ReindexFileStatus.FAILED:
      return (
        <TooltipTrigger
          content={reason || "Failed"}
          classes={styles.error_icon_tooltip_trigger}
        >
          <FailIcon aria-label={reason || "Failed"} />
        </TooltipTrigger>
      );
    case ReindexFileStatus.SKIPPED:
      return (
        <TooltipTrigger
          content={reason || "Skipped"}
          classes={styles.error_icon_tooltip_trigger}
        >
          <FailIcon aria-label={reason || "Skipped"} />
        </TooltipTrigger>
      );
    case ReindexFileStatus.QUEUED:
    default:
      return <WaitingIcon />;
  }
};

const getColumns = () => [
  {
    header: "File Name",
    accessorKey: "name",
    id: "name",
    size: 332,
    minSize: 150,
    cell: ({ row }) => {
      const file = row.original;
      return (
        <span className={styles.fileName} data-clarity-mask="True">
          {getDocumentIcon(file.name)}
          <Ellipsis text={file.name} />
          {getStatusIcon(file.status, file.reason)}
        </span>
      );
    },
    sortingFn: (a, b) =>
      a.original.name.localeCompare(b.original.name, "en", {
        numeric: true,
        sensitivity: "base",
      }),
  },
  {
    header: "Source",
    accessorKey: "source",
    id: "source",
    size: 90,
    minSize: 90,
    cell: ({ row }) => {
      const file = row.original;
      return (
        <span className={styles.fileSource}>
          <Ellipsis text={formatSource(file.source)} />
        </span>
      );
    },
    sortingFn: (a, b) =>
      formatSource(b.original.source).localeCompare(
        formatSource(a.original.source)
      ),
    enableResizing: false,
  },
];

const resolveTitle = (terminalState) =>
  TITLES[terminalState] ?? IN_PROGRESS_TITLE;

const resolveDescription = (terminalState) =>
  DESCRIPTIONS[terminalState] ?? IN_PROGRESS_DESCRIPTION;

const resolveFooterHint = (terminalState) =>
  terminalState === null
    ? FOOTER_HINTS.IN_PROGRESS
    : FOOTER_HINTS[terminalState];

const CollectionReindexProgressModal = ({ onCancelChat }) => {
  const dispatch = useDispatch();
  const { files, terminalState } = useSelector(selectReindexProgress);

  useEffect(() => {
    if (terminalState !== ReindexTerminalState.SUCCESS) {
      return undefined;
    }
    const timer = setTimeout(() => {
      dispatch(dismiss());
      dispatch(hideModal(modalTypes.COLLECTION_REINDEX_PROGRESS));
    }, AUTO_CLOSE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [terminalState, dispatch]);

  const closeAll = () => {
    dispatch(dismiss());
    dispatch(hideModal(modalTypes.COLLECTION_REINDEX_PROGRESS));
  };

  const handleCancel = () => {
    onCancelChat?.();
    closeAll();
  };

  const tableData = useMemo(
    () =>
      Object.entries(files).map(([id, f]) => ({
        ragDocumentId: id,
        name: f.title,
        source: f.source ?? null,
        status: f.status,
        reason: f.reason,
      })),
    [files]
  );

  const columns = useMemo(() => getColumns(), []);

  const renderFooterButton = () => {
    if (terminalState === null) {
      return (
        <NewButton variant={NewButtonVariant.l1} onClick={handleCancel}>
          Cancel
        </NewButton>
      );
    }
    if (terminalState === ReindexTerminalState.PARTIAL) {
      return <NewButton onClick={closeAll}>Continue</NewButton>;
    }
    if (terminalState === ReindexTerminalState.FAILED) {
      return (
        <NewButton variant={NewButtonVariant.l1} onClick={closeAll}>
          Close
        </NewButton>
      );
    }
    return null;
  };

  return (
    // onDismiss is intentionally a no-op: backdrop/escape must not dismiss
    // the modal. Users may only exit via Cancel (in-progress) or Continue/
    // Close (terminal states), so they explicitly acknowledge what happened.
    <Modal
      className={`upload_files_modal ${styles.reindexProgressModal}`}
      show
      backdrop
      onDismiss={() => undefined}
      restrictWidth={false}
    >
      <Modal.Header>
        <span>{resolveTitle(terminalState)}</span>
      </Modal.Header>
      <Modal.Body>
        <div className={styles.description}>
          {resolveDescription(terminalState)}
        </div>
        <div className={styles.tableWrapper}>
          <GenericTable
            data={tableData}
            columns={columns}
            loading={false}
            defaultSortColumnIndex={-1}
            rowIdKey="ragDocumentId"
            selectedIndex={-1}
            variant={GenericTableVariant.bordered}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <span
          className={
            terminalState === null
              ? `${styles.processingHint} ${styles.footerHint}`
              : styles.footerHint
          }
        >
          {resolveFooterHint(terminalState)}
        </span>
        {renderFooterButton()}
      </Modal.Footer>
    </Modal>
  );
};

CollectionReindexProgressModal.propTypes = {
  onCancelChat: PropTypes.func,
};

export default CollectionReindexProgressModal;
